import React from 'react';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { InlineText } from './InlineEditable';

export default function PageHero({ kicker, title, text, accent = 'text-coral-200', slug }) {
  const settings = usePublicSettings();

  // Convert React node title to HTML string fallback (for InlineText html mode)
  const titleHtml = typeof title === 'string' ? title : reactToHtml(title);

  return (
    <section className="relative bg-ink-600 text-cream overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9A96E 0, transparent 55%), radial-gradient(circle at 80% 70%, #E8826F 0, transparent 55%)' }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="max-w-4xl">
          <InlineText
            settingKey={slug ? `page_${slug}_kicker` : 'page_kicker_generic'}
            fallback={kicker || ''}
            as="div"
            className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold"
          />
          <InlineText
            settingKey={slug ? `page_${slug}_title` : 'page_title_generic'}
            fallback={titleHtml}
            as="h1"
            html
            multiline
            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-5"
          />
          <InlineText
            settingKey={slug ? `page_${slug}_text` : 'page_text_generic'}
            fallback={text || ''}
            as="p"
            multiline
            className="mt-6 text-cream/75 text-base md:text-lg max-w-2xl"
          />
        </div>
      </div>
    </section>
  );
}

// Convert a simple React node (string / fragment / span with class) to an
// HTML string so PageHero page callers can pass JSX for title but still be
// admin-editable (which needs raw HTML).
function reactToHtml(node) {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactToHtml).join('');
  if (node && node.props) {
    const { children, className } = node.props;
    const inner = reactToHtml(children);
    const tag = typeof node.type === 'string' ? node.type : 'span';
    if (tag === 'br') return '<br />';
    if (className) return `<${tag} class="${className}">${inner}</${tag}>`;
    return `<${tag}>${inner}</${tag}>`;
  }
  return '';
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="bg-cream border-b border-ink-100">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-12 flex items-center gap-2 text-xs text-ink-400">
        {items.map((it, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-ink-200">/</span>}
            {it.href ? <a href={it.href} className="hover:text-ink-600">{it.label}</a> : <span className="text-ink-600 font-medium">{it.label}</span>}
          </React.Fragment>
        ))}
      </div>
    </nav>
  );
}
